import React, { ChangeEvent, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Container,
  Paper,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  OutlinedInput,
  FormHelperText,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { COLORS } from '../../theme/themeConstants';
import 'react-quill-new/dist/quill.snow.css';
import MyEditor from './RichTextEditor';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useTranslation } from 'react-i18next';
interface FormField {
  value?: any;
  name: string;
  label?: string;
  type?: string;
  options?: { value: string; label: string }[];
  radioOptions?: string[];
  checkOptions?: string[];
  isRequired?: boolean;
  tooltip?: string;
  selectWidth?: any;
  selectLength?: boolean;
  placeholder?: string;
  dependsOn?: {
    field: string;
    value: string;
  };
  suffix?: {
    type: string;
    options: string[];
  };
  component?: (props: { field: any; form: any }) => React.ReactElement;
  sectionTitle?: string;
  sectionLine?: boolean;
  disabled?: boolean;
}

interface FormConfig {
  delivery?: string;
  title?: string;
  initialValues: Record<string, any>;
  validationSchema: Yup.ObjectSchema<any>;
  onSubmit: (values: Record<string, any>) => void;
  fields: FormField[];
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  enableReinitialize?: boolean;
  // customButton?: React.ReactNode;
  customButton?: React.ReactNode | ((formikProps: any) => React.ReactNode);
  validationContext?: Record<string, any>;
}

const allForm = (config: FormConfig) => {
  return () => {
    const [showPasswordMap, setShowPasswordMap] = useState<
      Record<string, boolean>
    >({});
    const { i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    const handleClickShowPassword = (name: string) => {
      setShowPasswordMap((prevMap) => ({
        ...prevMap,
        [name]: !prevMap[name],
      }));
    };

    const handleMouseDownPassword = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault();
    };

    return (
        <Container maxWidth="lg" sx={{ py: 2 }}>
          {config.title && (
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              {config.title}
            </Typography>
          )}
          {/* Form Container */}
          <Paper
            elevation={1}
            sx={{
              p: 4,
              mt: 2,
              bgcolor: '#fff',
              borderRadius: 1,
              border: '1px solid #ddd',
            }}
          >
            <Formik
              initialValues={config.initialValues}
              validationSchema={config.validationSchema}
              onSubmit={config.onSubmit}
              validateOnChange={config.validateOnChange}
              validateOnBlur={config.validateOnBlur}
              enableReinitialize={config.enableReinitialize}
            >
              {({
                values,
                setFieldValue,
                setFieldTouched,
                errors,
                touched,
                resetForm,
                handleSubmit,
                dirty,
                isValid,
              }) => (
                <Form>
                  {config.fields.map(
                    ({
                      name,
                      label,
                      type = 'text',
                      options,
                      radioOptions,
                      checkOptions,
                      isRequired,
                      dependsOn,
                      suffix,
                      selectWidth,
                      component,
                      sectionTitle,
                      sectionLine,
                      disabled = false,
                      placeholder,
                    }) => {
                      if (type === 'section') {
                        return (
                          <Box key={sectionTitle} sx={{ mb: 2, mt: 3 }}>
                            {sectionTitle && (
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: 20,
                                  marginBottom: 8,
                                  fontFamily: 'Livvic',
                                }}
                              >
                                {sectionTitle}
                              </div>
                            )}
                            {sectionLine && <hr style={{ marginBottom: 16 }} />}
                          </Box>
                        );
                      }
                      if (
                        dependsOn &&
                        values[dependsOn.field] !== dependsOn.value
                      ) {
                        return null;
                      }

                      return (
                        <Box key={name} sx={{ mb: 3 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: { xs: 1, sm: 0 },
                              justifyContent: 'center',
                            }}
                          >
                            <FormLabel
                              htmlFor={name}
                              required={isRequired}
                              sx={{
                                width: label
                                  ? { xs: '100%', sm: '180px' }
                                  : '0',
                                color: COLORS.text.primary,
                                fontSize: 14,
                                fontWeight: 500,
                                textAlign: { xs: 'left', sm: 'right' },
                                pr: { xs: 0, sm: 2 },
                                pt: { xs: 0, sm: 1 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: {
                                  xs: 'flex-start',
                                  sm: 'flex-end',
                                },
                                '& .MuiFormLabel-asterisk': {
                                  color: 'red',
                                },
                              }}
                            >
                              {label}
                            </FormLabel>
                            <Box
                              sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                ml: label ? 0 : '89px',
                              }}
                            >
                              {component ? (
                                <>
                                  <Field name={name}>
                                    {({ field, form }: any) =>
                                      component({ field, form })
                                    }
                                  </Field>
                                  {/* Only show error message for custom type fields that are not deliveryChargeSetting */}
                                  {type === 'custom' ||
                                    (type === 'nested' &&
                                      // name !== "deliveryChargeSetting" &&
                                      touched[name] &&
                                      errors[name] && (
                                        // <FormHelperText error sx={{ mt: 0.5 }}>
                                        //   {typeof errors[name] === 'object'
                                        //     ? JSON.stringify(errors[name])
                                        //     : errors[name]}
                                        // </FormHelperText>
                                        <></>
                                      ))}
                                </>
                              ) : type === 'password' ||
                                type === 'passwordVisibility' ? (
                                <FormControl fullWidth>
                                  <TextField
                                    className="input-large"
                                    name={name}
                                    type={
                                      showPasswordMap[name]
                                        ? 'text'
                                        : 'password'
                                    }
                                    value={values[name]}
                                    onChange={(e) =>
                                      setFieldValue(name, e.target.value)
                                    }
                                    onBlur={() => setFieldTouched(name, true)}
                                    error={Boolean(
                                      touched[name] && errors[name]
                                    )}
                                    placeholder={
                                      placeholder
                                        ? placeholder
                                        : label === 'Confirm Password:'
                                        ? 'Enter Password Again'
                                        : `Enter ${label?.replace(/:$/, '') || ''}`
                                    }
                                    sx={{
                                      mb: 0,
                                      '& .MuiInputBase-root': {
                                        fontSize: 14,
                                        color: 'text.primary',
                                        width: '400px',
                                        height: '40px',
                                      },
                                      '& .MuiFormLabel-root': {
                                        fontSize: 14,
                                      },
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            onClick={() =>
                                              handleClickShowPassword(name)
                                            }
                                            onMouseDown={
                                              handleMouseDownPassword
                                            }
                                            edge="end"
                                          >
                                            {showPasswordMap[name] ? (
                                              <Visibility />
                                            ) : (
                                              <VisibilityOff />
                                            )}
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </FormControl>
                              ) : type === 'number' ? (
                                <FormControl fullWidth>
                                  <TextField
                                    className="input-large"
                                    name={name}
                                    type="text" // Use text for custom filtering
                                    value={values[name] ?? ''}
                                    // onChange={(e) => {
                                    //   const inputValue = e.target.value;
                                    //   if (/^\d*$/.test(inputValue)) {
                                    //     setFieldValue(name, inputValue);
                                    //   }
                                    // }}
                                    onChange={(e) => {
                                      const inputValue = e.target.value;
                                      // Allow numbers with up to 2 decimal places (e.g., "10", "10.5", "10.50")
                                      if (inputValue === '' || /^\d*(\.\d{0,2})?$/.test(inputValue)) {
                                        setFieldValue(name, inputValue);
                                        setFieldTouched(name, true, false);
                                      }
                                    }}
                                    onBlur={() => setFieldTouched(name, true)}
                                    error={Boolean(
                                      touched[name] && errors[name]
                                    )}
                                    placeholder={
                                      placeholder
                                        ? placeholder
                                        : `Enter ${label?.replace(/:$/, '') || ''}`
                                    }
                                    inputProps={{
                                      inputMode: 'decimal',
                                      pattern: '[0-9]*\\.?[0-9]*',
                                    }}
                                    sx={{
                                      mb: 0,
                                      '& .MuiInputBase-root': {
                                        fontSize: 14,
                                        color: 'text.primary',
                                        width: '400px',
                                        height: '40px',
                                      },
                                      '& .MuiFormLabel-root': {
                                        fontSize: 14,
                                      },
                                    }}
                                  />
                                </FormControl>
                              ) : type === 'mobile' ? (
                                <FormControl
                                  fullWidth
                                  error={touched[name] && Boolean(errors[name])}
                                  sx={{
                                    width: '400px',
                                    '& .react-tel-input': {
                                      width: '100%',
                                    },
                                    '& .form-control': {
                                      width: '100% !important',
                                      height: '40px !important',
                                      paddingLeft: '48px !important',
                                      boxSizing: 'border-box',
                                      fontSize: '14px',
                                      borderRadius: '4px',
                                      color: 'rgba(0, 0, 0, 0.87)',
                                    },
                                    '& .flag-dropdown': {
                                      border: 'none',
                                      background: 'transparent',
                                      height: '40px',
                                      borderRadius: '4px 0 0 4px',
                                      borderRight: 'none',
                                      '&.open': {
                                        backgroundColor: 'transparent',
                                      },
                                    },
                                    '& .selected-flag': {
                                      height: '40px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      backgroundColor: 'transparent !important',
                                      '&:hover': {
                                        backgroundColor:
                                          'transparent !important',
                                      },
                                    },
                                    '& .country-list': {
                                      marginTop: '4px',
                                    },
                                  }}
                                >
                                  <PhoneInput
                                    country={'pk'}
                                    onlyCountries={[
                                      'jo',
                                      'sa',
                                      'ae',
                                      'kw',
                                      'qa',
                                      'bh',
                                      'om',
                                      'pk',
                                    ]}
                                    value={values[name]}
                                    onChange={(value) => {
                                      const numericValue = value.replace(
                                        /\D/g,
                                        ''
                                      );
                                      setFieldValue(name, numericValue);
                                    }}
                                    onBlur={() => setFieldTouched(name, true)}
                                    specialLabel=""
                                    placeholder="Enter Mobile No"
                                    inputClass="input-large"
                                    inputProps={{
                                      name,
                                      required: true,
                                      autoFocus: false,
                                      style: {
                                        width: '100%',
                                        height: '40px',
                                        fontSize: '14px',
                                        paddingLeft: '48px',
                                        borderRadius: '4px',
                                        border:
                                          touched[name] && Boolean(errors[name])
                                            ? '1px solid #d32f2f'
                                            : '1px solid #ccc',
                                        backgroundColor: '#fff',
                                      },
                                    }}
                                    containerStyle={{
                                      width: '100%',
                                    }}
                                    buttonStyle={{
                                      border: 'none',
                                      background: 'transparent',
                                      paddingLeft: '10px',
                                      height: '40px',
                                      borderRadius: '4px 0 0 4px',
                                    }}
                                  />
                                </FormControl>
                              ) : type === 'select' || options ? (
                                <FormControl
                                  sx={{ width: selectWidth || '300px' }}
                                >
                                  <Select
                                    id={name}
                                    name={name}
                                    value={values[name]}
                                    onBlur={() => setFieldTouched(name, true)}
                                    onChange={(e: SelectChangeEvent<string>) =>
                                      setFieldValue(name, e.target.value)
                                    }
                                    displayEmpty
                                    input={
                                      config.fields.find((f) => f.name === name)
                                        ?.selectLength ? (
                                        <OutlinedInput
                                          className="input-large"
                                          sx={{ height: '40px', width: '100%' }}
                                        />
                                      ) : (
                                        <OutlinedInput
                                          notched={false}
                                          sx={{
                                            height: '40px',
                                            fontWeight: '500',
                                          }}
                                        />
                                      )
                                    }
                                    sx={{
                                      '& .MuiSelect-select': {
                                        color: 'rgba(0, 0, 0, 0.6)',
                                      },
                                    }}
                                  >
                                    {/* <MenuItem value="">-- Select --</MenuItem> */}
                                    <MenuItem value="" disabled>
                                      <span
                                        style={{ color: 'rgba(0, 0, 0, 0.6)' }}
                                      >
                                        -- Select --
                                      </span>
                                    </MenuItem>
                                    {(options|| []).map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                        sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : type === 'radio' && radioOptions ? (
                                <FormControl>
                                  <RadioGroup
                                    row
                                    name={name}
                                    value={values[name]}
                                    onBlur={() => setFieldTouched(name, true)}
                                    onChange={(
                                      e: ChangeEvent<HTMLInputElement>
                                    ) => setFieldValue(name, e.target.value)}
                                    sx={{ gap: 2.5, mt: 0.5 }}
                                  >
                                    {radioOptions.map((option) => (
                                      <FormControlLabel
                                        key={option}
                                        value={option}
                                        control={
                                          <Radio
                                            size="small"
                                            sx={{
                                              color: 'text.secondary',
                                              '&.Mui-checked': {
                                                color: 'primary.main',
                                              },
                                            }}
                                          />
                                        }
                                        label={
                                          <Typography
                                            sx={{
                                              fontSize: 14,
                                              color: COLORS.text.secondary,
                                            }}
                                          >
                                            {option}
                                          </Typography>
                                        }
                                        sx={{
                                          margin: 0,
                                          '& .MuiFormControlLabel-label': {
                                            fontSize: 14,
                                            color: 'text.secondary',
                                          },
                                        }}
                                      />
                                    ))}
                                  </RadioGroup>
                                </FormControl>
                              ) : type === 'checkbox' && checkOptions ? (
                                <FormControl>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 2.5,
                                      mt: 0.5,
                                    }}
                                  >
                                    {checkOptions.map((option) => (
                                      <FormControlLabel
                                        key={option}
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={
                                              Array.isArray(values[name])
                                                ? values[name].includes(option)
                                                : false
                                            }
                                            onBlur={() =>
                                              setFieldTouched(name, true)
                                            }
                                            onChange={(
                                              e: ChangeEvent<HTMLInputElement>
                                            ) => {
                                              const currentValues =
                                                Array.isArray(values[name])
                                                  ? values[name]
                                                  : [];
                                              if (e.target.checked) {
                                                setFieldValue(name, [
                                                  ...currentValues,
                                                  option,
                                                ]);
                                              } else {
                                                setFieldValue(
                                                  name,
                                                  currentValues.filter(
                                                    (v: string) => v !== option
                                                  )
                                                );
                                              }
                                            }}
                                            sx={{
                                              color: 'text.secondary', // Default color when unchecked
                                              '&.Mui-checked': {
                                                color: 'primary.main', // Blue color when checked
                                              },
                                            }}
                                          />
                                        }
                                        label={
                                          <Typography
                                            sx={{
                                              fontSize: 14,
                                              color: 'text.secondary',
                                            }}
                                          >
                                            {option}
                                          </Typography>
                                        }
                                        sx={{
                                          margin: 0,
                                          '& .MuiFormControlLabel-label': {
                                            fontSize: 14,
                                            color: 'text.secondary',
                                          },
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </FormControl>
                              ) : type === 'richtext' ? (
                                <FormControl
                                  fullWidth
                                  sx={{ mt: 2 }}
                                  error={touched[name] && Boolean(errors[name])}
                                >
                                  <Box
                                    sx={{
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      overflow: 'hidden',
                                      '.ql-container': {
                                        border: 'none',
                                        fontFamily: 'inherit',
                                      },
                                      '.ql-toolbar': {
                                        borderBottom: '1px solid #ddd',
                                      },
                                      '.ql-editor': {
                                        minHeight: '150px',
                                        padding: '12px',
                                      },
                                    }}
                                  >
                                    <MyEditor
                                      value={values[name]}
                                      onChange={(val) =>
                                        setFieldValue(name, val)
                                      }
                                      onBlur={() =>
                                        setFieldTouched(name, true, true)
                                      }
                                    />
                                  </Box>
                                  {/* {touched[name] && errors[name] && (
                                    <FormHelperText sx={{ mt: 0.5 }}>
                                      {typeof errors[name] === 'object'
                                        ? 'Please check the input'
                                        : errors[name]}
                                    </FormHelperText>
                                  )} */}
                                </FormControl>
                              ) : type === 'text' ? (
                                <FormControl fullWidth>
                                  <TextField
                                    className="input-large"
                                    name={name}
                                    value={values[name]}
                                    onChange={(e) => {
                                      setFieldValue(name, e.target.value);
                                      setFieldTouched(name, true, false);
                                    }}
                                    onBlur={() => setFieldTouched(name, true)}
                                    error={Boolean(
                                      touched[name] && errors[name]
                                    )}
                                    placeholder={
                                      placeholder
                                        ? placeholder
                                        : `Enter ${label?.replace(/:$/, '') || ''}`
                                    }
                                    disabled={disabled}
                                    sx={{
                                      mb: 0,
                                      '& .MuiInputBase-root': {
                                        fontSize: 14,
                                        color: 'text.primary',
                                        width: '400px',
                                        height: '40px',
                                      },
                                      '& .MuiFormLabel-root': {
                                        fontSize: 14,
                                      },
                                    }}
                                  />
                                </FormControl>
                              ) : type === 'customSelect' && component ? (
                                <Field name={name} key={name}>
                                  {({ field, form }: any) => (
                                    <>
                                      {React.createElement(component, {
                                        field,
                                        form,
                                        value: field.value, // optional, component can read from field.value
                                        onChange: (value: any) =>
                                          form.setFieldValue(name, value),
                                      })}
                                      {touched[name] && errors[name] && (
                                        <FormHelperText error sx={{ mt: 0.5 }}>
                                          {typeof errors[name] === 'object'
                                            ? 'Please check the input'
                                            : errors[name]}
                                        </FormHelperText>
                                      )}
                                    </>
                                  )}
                                </Field>
                              ) : (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <OutlinedInput
                                    id={name}
                                    name={name}
                                    type={type}
                                    value={values[name]}
                                    onChange={(
                                      e: ChangeEvent<HTMLInputElement>
                                    ) => setFieldValue(name, e.target.value)}
                                    fullWidth
                                    sx={{
                                      height: '40px',
                                      width: suffix
                                        ? 'calc(100% - 100px)'
                                        : '100%',
                                    }}
                                  />
                                  {suffix && (
                                    <Select
                                      value={values[`${name}Unit`]}
                                      onChange={(
                                        e: SelectChangeEvent<string>
                                      ) =>
                                        setFieldValue(
                                          `${name}Unit`,
                                          e.target.value
                                        )
                                      }
                                      input={
                                        <OutlinedInput
                                          sx={{
                                            height: '40px',
                                            ml: 1,
                                            width: '90px',
                                          }}
                                        />
                                      }
                                    >
                                      {suffix.options.map((option) => (
                                        <MenuItem key={option} value={option}>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  )}
                                </Box>
                              )}

                              {/* Show error message for non-custom type fields */}
                              {type !== 'custom' && type !== 'nested' && (
                                <ErrorMessage name={name}>
                                  {(msg) => (
                                    <FormHelperText
                                      error
                                      sx={{
                                        mt: 0,
                                        fontSize: 12,
                                        lineHeight: 1.2,
                                        textAlign: isRTL ? 'right' : 'left',
                                        direction: isRTL ? 'rtl' : 'ltr',
                                      }}
                                    >
                                      {msg}
                                    </FormHelperText>
                                  )}
                                </ErrorMessage>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                  )}
                  <Box
                    sx={{
                      mt: 3,
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    {typeof config.customButton === 'function'
                      ? config.customButton({
                          values,
                          setFieldValue,
                          setFieldTouched,
                          errors,
                          touched,
                          resetForm,
                          handleSubmit,
                          dirty,
                          isValid,
                        })
                      : config.customButton}
                  </Box>
                </Form>
              )}
            </Formik>
          </Paper>
        </Container>
    );
  };
};

export default allForm;
